import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { BrandClub, Page, Retailer, Site, UserProfile } from '@brandclub/types';
import { AxiosInstance } from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnalyticsTrackingEvent } from '../analytics/events';
import { TrackingParams } from '../types/AnalyticsTracking';
import { CheckoutEligibility, ProductRewardSearchResult } from '../types/product';

type LinkComponentType = typeof Link;

export enum StoreBrandingType {
  CustomDTCStore = 'CustomDTCStore',
  CustomStore = 'CustomStore',
  BrandStore = 'BrandStore',
  UniversalStore = 'UniversalStore',
}

export enum SiteBrandingType {
  CustomDTCStore = 'CustomDTCStore',
  CustomStore = 'CustomStore',
  BrandStore = 'BrandStore',
  UniversalStore = 'UniversalStore',
}

export type FooterLinkSection = {
  title?: string;
  titleLink?: string;
  links: {
    label: string;
    link: string;
    offsetText?: string;
    onClick?: (e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  }[];
};
export interface IAppDomainConfig {
  domainName: string;
  brandId: number;
  brandName: string;
  storeBrandingType: StoreBrandingType;
  checkoutAllowedOfferType?: 'firstPartyOnly' | 'thirdPartyOnly' | 'both';
}

export interface BaseComponentPropsType {
  LinkComponent: LinkComponentType;
  useSearchParams: typeof useSearchParams;
  useNavigate: typeof useNavigate;
  mainEntity: {
    type?: string;
    entity: any;
    id?: string | number;
  } | null;
  brandEntity: {
    type?: string;
    entity: any;
    id?: string | number;
    brandclub?: any;
  } | null;
  userProfile?: UserProfile | null;
  retailers?: Partial<Retailer>[];
  appConfig?: {
    domainConfig?: IAppDomainConfig;
  };
  trackingParams: TrackingParams;
  persistentQueryParams?: string;
  analyticsTrack?: (eventName: AnalyticsTrackingEvent, eventProps: { [key: string]: any }) => Promise<void>;
  axios: AxiosInstance;
  axiosEdgeCached: AxiosInstance;
  apolloClient: ApolloClient<NormalizedCacheObject>;
  pageContext: {
    brandId?: string;
    categoryId?: string;
    subCategoryId?: string;
    sku?: string;
    categoryEntity?: {
      type: string;
      entity: any;
      id: string | number;
    };
    subCategoryEntity?: {
      type: string;
      entity: any;
      id: string | number;
    };
    abTestingTreatments?: {
      [key: string]: string;
    };
  };
  site: Site;
  page: Page;
  brandclub?: BrandClub;
  isInternalCompany?: boolean;
  routePrefix: string;
  customerSpendByBrand?: {
    brandIds: number[];
    brandclubIds: string[];
    spendByBrandId: {
      [brandId: number]: {
        brandId: number;
        brandName: string;
        brandclubId?: string;
        spend: number;
      };
    };
    loading: boolean;
  };
  onShareClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, product?: ProductRewardSearchResult) => void;
  onAddToCart?: (product: {
    brandId: number;
    retailerSku: string;
    retailerId: number;
    stacklineSku: string;
    retailPrice: number;
    retailerProductPageUrl: string;
    checkoutEligibility?: CheckoutEligibility;
  }) => void;
  handleJoinClub?: (brandclubId: string, campaignId?: string) => Promise<'success' | 'failed' | undefined>;
  footerConfig?: {
    siteBrandingType: SiteBrandingType;
    linkSections: FooterLinkSection[];
    customText?: string;
    /** If true, hide the brand section (name, description) in the footer */
    hideBrandSection?: boolean;
  };
}
